import { createBuffer } from "../encrypt/create-buffer";
import { CADESCOM_STORE, X509ENROLLMENT_CX509ENROLLMENT } from "../constants";
import { splitName, sendError, shouldSendError } from "./utils";

export function getCryptoProInfo(
  setCryptoProInfo,
  isCryptoProInstalled,
  isCryptoProChecked,
  onError
) {
  try {
    cadesplugin.async_spawn(function* checkVersion() {
      try {
        const oAbout = yield cadesplugin.CreateObjectAsync("CAdESCOM.About");
        const oVersion = yield oAbout.CSPVersion("", 80);
        const plugin = yield oAbout.PluginVersion;
        const cspVersion = `${yield oVersion.MajorVersion}.${yield oVersion.MinorVersion}.${yield oVersion.BuildVersion}`;
        const pluginVersion = yield plugin.toString();

        yield setCryptoProInfo(`plugin: ${pluginVersion}, CSP: ${cspVersion}`);
        if (pluginVersion && cspVersion) {
          yield isCryptoProInstalled(true);
        }

        yield isCryptoProChecked(true);
      } catch (error) {
        yield isCryptoProInstalled(false);
        yield isCryptoProChecked(true);
        if (shouldSendError(error)) {
          yield sendError(error);
        }

        if (typeof onError === "function") {
          yield onError();
        }
      }
    });
  } catch (error) {
    isCryptoProInstalled(false);
    isCryptoProChecked(true);
    if (typeof onError === "function") {
      onError();
    }

    if (shouldSendError(error)) {
      sendError(error);
    }
  }
}

export function getCertificateList(result, onError) {
  cadesplugin.async_spawn(function* () {
    try {
      const oMyStore = yield cadesplugin.CreateObjectAsync(CADESCOM_STORE);
      const CAPICOM_CURRENT_USER_STORE = 2;
      const CAPICOM_MY_STORE = "My";
      const CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED = 2;

      yield oMyStore.Open(
        CAPICOM_CURRENT_USER_STORE,
        CAPICOM_MY_STORE,
        CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED
      );

      const myCertificates = yield oMyStore.Certificates;
      const certificatesList = [];
      const myCertificatesCount = yield myCertificates.Count;

      for (let i = 1; i <= myCertificatesCount; i++) {
        try {
          const certificate = yield myCertificates.Item(i);

          const serialNumber = yield certificate.SerialNumber;
          const issuer = splitName(yield certificate.IssuerName);
          const subject = splitName(yield certificate.SubjectName);

          const preparedCertificate = {
            serialNumber,
            Issuer: issuer,
            Subject: subject,
          };

          certificatesList.push(preparedCertificate);
        } catch (error) {
          if (shouldSendError(error)) {
            yield sendError(error);
          }

          return `[CryptoProAsync] Certificate error: ${yield error.toString()}`;
        }
      }

      const oRootStore = yield cadesplugin.CreateObjectAsync(CADESCOM_STORE);
      const CAPICOM_ROOT_STORE = "Root";

      yield oRootStore.Open(
        CAPICOM_CURRENT_USER_STORE,
        CAPICOM_ROOT_STORE,
        CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED
      );
      const rootCertificates = yield oRootStore.Certificates;
      const rootCertificatesCount = yield rootCertificates.Count;

      for (let j = 1; j <= rootCertificatesCount; j++) {
        try {
          const certificate = yield rootCertificates.Item(j);

          const serialNumber = yield certificate.SerialNumber;
          const issuer = splitName(yield certificate.IssuerName);
          const subject = splitName(yield certificate.SubjectName);

          const preparedCertificate = {
            serialNumber,
            Issuer: issuer,
            Subject: subject,
          };

          certificatesList.push(preparedCertificate);
        } catch (error) {
          if (shouldSendError(error)) {
            yield sendError(error);
          }

          return `[CryptoProAsync] Certificate error: ${yield error.toString()}`;
        }
      }

      yield result(certificatesList);
    } catch (error) {
      const lastError = cadesplugin.getLastError(error);

      yield onError(lastError);
      if (shouldSendError(error)) {
        yield sendError(error);
      }
    }
  });
}

export function signCertificate(
  serialNumber,
  dataToSign,
  result,
  onError,
  isCachePrivateKeyPin = false
) {
  cadesplugin.async_spawn(function* () {
    let Signature;

    try {
      const oStore = yield cadesplugin.CreateObjectAsync(CADESCOM_STORE);

      yield oStore.Open();
      const all_certs = yield oStore.Certificates;
      let cert;

      for (let i = 1; i <= (yield all_certs.Count); i++) {
        cert = yield all_certs.Item(i);

        if (
          serialNumber === (yield cert.SerialNumber) &&
          Date.parse(yield cert.ValidToDate) > Date.now()
        ) {
          break;
        } else {
          cert = null;
        }
      }

      if (cert != null) {
        // Запоминаем пароль от контейнера закрытого ключа, чтобы не вводить повторно
        const certPrivateKey = yield cert.PrivateKey;

        yield certPrivateKey.propset_CachePin(isCachePrivateKeyPin);

        const oSigner = yield cadesplugin.CreateObjectAsync(
          "CAdESCOM.CPSigner"
        );

        yield oSigner.propset_Certificate(cert);
        yield oSigner.propset_Options(
          cadesplugin.CAPICOM_CERTIFICATE_INCLUDE_WHOLE_CHAIN
        );
        yield oSigner.propset_CheckCertificate(true);

        const oSignedData = yield cadesplugin.CreateObjectAsync(
          "CAdESCOM.CadesSignedData"
        );

        yield oSignedData.propset_ContentEncoding(
          cadesplugin.CADESCOM_BASE64_TO_BINARY
        );
        yield oSignedData.propset_Content(dataToSign);
        Signature = yield oSignedData.SignCades(
          oSigner,
          cadesplugin.CADESCOM_CADES_BES,
          true
        );
        yield oStore.Close();
        yield result(Signature);
      } else {
        yield onError("Сертификаты не найдены");
      }
    } catch (error) {
      yield onError(error.message);
      yield sendError(error);
    }
  });
}

export function releasePluginObjects() {
  return cadesplugin.ReleasePluginObjects();
}

export function createCertificateApplication(
  distinguishedName,
  setCryptoParams,
  onError
) {
  cadesplugin.async_spawn(function* createCert() {
    let cryptoproParams = {};

    try {
      const XCN_AT_KEYEXCHANGE = 1;
      const encodingTypeNum = 0x1;
      const PKey = yield cadesplugin.CreateObjectAsync(
        "X509Enrollment.CX509PrivateKey"
      );

      yield PKey.propset_ProviderName(
        "Crypto-Pro GOST R 34.10-2012 Cryptographic Service Provider"
      );
      yield PKey.propset_ProviderType(80);
      yield PKey.propset_KeySpec(XCN_AT_KEYEXCHANGE);

      const CertificateRequestPkcs10 = yield cadesplugin.CreateObjectAsync(
        "X509Enrollment.CX509CertificateRequestPkcs10"
      );

      yield CertificateRequestPkcs10.InitializeFromPrivateKey(0x1, PKey, "");

      const DistinguishedName = yield cadesplugin.CreateObjectAsync(
        "X509Enrollment.CX500DistinguishedName"
      );

      yield DistinguishedName.Encode(distinguishedName);
      CertificateRequestPkcs10.propset_Subject(DistinguishedName);

      const KeyUsageExtension = yield cadesplugin.CreateObjectAsync(
        "X509Enrollment.CX509ExtensionKeyUsage"
      );

      const CERT_DATA_ENCIPHERMENT_KEY_USAGE = 0x10;
      const CERT_KEY_ENCIPHERMENT_KEY_USAGE = 0x20;
      const CERT_DIGITAL_SIGNATURE_KEY_USAGE = 0x80;
      const CERT_NON_REPUDIATION_KEY_USAGE = 0x40;

      yield KeyUsageExtension.InitializeEncode(
        CERT_KEY_ENCIPHERMENT_KEY_USAGE |
          CERT_DATA_ENCIPHERMENT_KEY_USAGE |
          CERT_DIGITAL_SIGNATURE_KEY_USAGE |
          CERT_NON_REPUDIATION_KEY_USAGE
      );
      const extensions = yield CertificateRequestPkcs10.X509Extensions;

      yield extensions.Add(KeyUsageExtension);

      const Enroll = yield cadesplugin.CreateObjectAsync(
        X509ENROLLMENT_CX509ENROLLMENT
      );

      yield Enroll.InitializeFromRequest(CertificateRequestPkcs10);

      const certificateRequest = yield Enroll.CreateRequest(encodingTypeNum);
      //const uniqueContainerName = yield PKey.UniqueContainerName;
      const ContainerName = yield PKey.ContainerName;

      const data = certificateRequest
        .replace("-----BEGIN CERTIFICATE-----", "")
        .replace("-----END CERTIFICATE-----", "")
        .replaceAll(/[\r\n]/g, "");

      cryptoproParams = {
        uuid: ContainerName,
        data: createBuffer().pack(data).toBase64String(),
        status: "success",
        reason: "CRYPTO_PLUGIN_SUCCESS",
        // containerName: ContainerName,
        // uniqueContainerName: uniqueContainerName,
      };
      yield setCryptoParams(cryptoproParams);
    } catch (error) {
      cryptoproParams = {
        status: "failure",
        reason: "CRYPTO_PLUGIN_FAILURE",
      };
      yield setCryptoParams(cryptoproParams);
      yield onError(error.message);
      yield sendError(error);
    }
  });
}

export function installCertificate(
  dataFromVerificationCenter,
  sendResultOfInstallingCertificate,
  onError
) {
  cadesplugin.async_spawn(function* install() {
    const decodedData = createBuffer()
      .decodeFromBase64(dataFromVerificationCenter)
      .unpack();

    try {
      const Enroll = yield cadesplugin.CreateObjectAsync(
        X509ENROLLMENT_CX509ENROLLMENT
      );

      yield Enroll.Initialize(0x1);
      yield Enroll.InstallResponse(0, decodedData, 0x7, "");
      yield sendResultOfInstallingCertificate({
        status: "success",
        reason: "CRYPTO_PLUGIN_SUCCESS",
      });
    } catch (error) {
      yield sendResultOfInstallingCertificate({
        status: "failure",
        reason: error?.message || "CRYPTO_PLUGIN_FAILURE",
      });

      if (typeof onError === "function") {
        yield onError(error.message);
      }

      yield sendError(error);
    }
  });
}

export function installRootCertificate(
  certificateInBase64,
  onSuccess,
  onError
) {
  return new Promise(function (resolve, reject) {
    cadesplugin.async_spawn(
      function* () {
        try {
          const store = yield cadesplugin.CreateObjectAsync("CAdESCOM.Store");

          const certificate = yield cadesplugin.CreateObjectAsync(
            "CAPICOM.Certificate"
          );

          yield certificate.Import(certificateInBase64);

          yield store.Open(
            cadesplugin.CAPICOM_CURRENT_USER_STORE,
            cadesplugin.CAPICOM_ROOT_STORE,
            cadesplugin.CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED
          );

          yield store.Add(certificate);

          yield onSuccess({
            status: "success",
            reason: "CRYPTO_PLUGIN_SUCCESS",
          });

          yield store.Close();
        } catch (error) {
          const lastError = cadesplugin.getLastError(error);

          if (typeof onError === "function") {
            yield onError(lastError);
          }

          yield sendError(lastError);
        }
      },
      resolve,
      reject
    );
  });
}

export function installCertificateInContainer(
  certificateInBase64,
  onSuccess,
  onError
) {
  return new Promise(function (resolve, reject) {
    cadesplugin.async_spawn(
      function* () {
        try {
          const enrollment = yield cadesplugin.CreateObjectAsync(
            X509ENROLLMENT_CX509ENROLLMENT
          );

          yield enrollment.Initialize(cadesplugin.ContextUser);
          yield enrollment.InstallResponse(
            cadesplugin.CADESCOM_UseContainerStore |
              cadesplugin.CADESCOM_InstallCertChainToContainer |
              cadesplugin.CADESCOM_AllowNone,
            certificateInBase64,
            cadesplugin.XCN_CRYPT_STRING_BASE64_ANY,
            ""
          );

          yield onSuccess({
            status: "success",
            reason: "CRYPTO_PLUGIN_SUCCESS",
          });
        } catch (error) {
          const lastError = cadesplugin.getLastError(error);

          if (typeof onError === "function") {
            yield onError(lastError);
          }

          yield sendError(lastError);
        }
      },
      resolve,
      reject
    );
  });
}
